import { useState, FormEvent } from 'react';
import { useTheme } from '@/context/ThemeContext';

const FORM_URL = 'https://readdy.ai/api/form/d7bdtjomqd64j87imjo0';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));

    setStatus('sending');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass = `w-full rounded-xl px-4 py-3.5 font-grotesk text-sm focus:outline-none transition-colors ${
    isDark
      ? 'bg-[#161B22] border border-white/8 text-white placeholder-white/20 focus:border-[#38D6D6]/50'
      : 'bg-[#F5F5F7] border border-black/8 text-[#1D1D1F] placeholder-black/25 focus:border-[#38D6D6]/50'
  }`;

  const labelClass = `block font-grotesk text-xs mb-2 tracking-wide uppercase ${isDark ? 'text-white/40' : 'text-black/40'}`;

  return (
    <section
      id="contact"
      className={`relative py-28 md:py-36 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#050505]' : 'bg-white'}`}
    >
      <div className={`absolute top-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-[#38D6D6]/20 to-transparent' : 'bg-gradient-to-r from-transparent via-[#38D6D6]/30 to-transparent'}`} />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#38D6D6]/60"></div>
              <span className="text-xs font-grotesk text-[#38D6D6] tracking-widest uppercase">Get Started</span>
            </div>
            <h2 className={`font-sora font-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-7 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
              Get Started
            </h2>
            <p className={`font-grotesk text-lg leading-relaxed mb-10 ${isDark ? 'text-white/55' : 'text-[#1D1D1F]/55'}`}>
              Book a 15-minute system audit. We&apos;ll identify exactly where your business is losing revenue and show you how to fix it.
            </p>

            {/* What you get */}
            <div className="flex flex-col gap-4 mb-10">
              {[
                { icon: 'ri-time-line', title: '15-minute audit call', desc: 'Fast, focused, no fluff' },
                { icon: 'ri-shield-check-line', title: 'No commitment required', desc: 'See the system before you decide' },
                { icon: 'ri-bar-chart-line', title: 'Custom revenue gap analysis', desc: 'Know exactly what you\'re losing' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#38D6D6]/10 border border-[#38D6D6]/20 flex-shrink-0">
                    <i className={`${item.icon} text-[#38D6D6] text-base`}></i>
                  </div>
                  <div>
                    <p className={`font-grotesk font-600 text-sm ${isDark ? 'text-white/85' : 'text-[#1D1D1F]/85'}`}>{item.title}</p>
                    <p className={`font-grotesk text-xs mt-0.5 ${isDark ? 'text-white/40' : 'text-black/40'}`}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Visual */}
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/79e52e09133a184160b0de2ab44762cd.jpg"
                alt="Book a demo consultation"
                className="w-full h-52 object-cover object-top"
              />
              <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#050505]/80 to-transparent' : 'bg-gradient-to-t from-white/60 to-transparent'}`}></div>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className={`rounded-2xl border p-8 md:p-10 ${isDark ? 'border-white/8 bg-[#0D1117]' : 'border-black/8 bg-[#F5F5F7]'}`}
            >
              <h3 className={`font-sora font-700 text-xl mb-7 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>Schedule My Demo</h3>

              <div className="flex flex-col gap-5">
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input type="text" name="name" required placeholder="John Smith" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Email Address</label>
                  <input type="email" name="email" required placeholder="john@company.com" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Phone Number</label>
                  <input type="tel" name="phone" placeholder="+1 (555) 000-0000" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Business Name</label>
                  <input type="text" name="business_name" required placeholder="Your Company LLC" className={inputClass} />
                </div>

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-4 rounded-xl font-grotesk font-600 text-base bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF] text-black hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60 mt-2"
                >
                  {status === 'sending' ? 'Sending...' : 'Schedule My Demo'}
                </button>

                {status === 'success' && (
                  <div className="flex items-center gap-2 p-4 rounded-xl bg-[#38D6D6]/10 border border-[#38D6D6]/20">
                    <i className="ri-check-line text-[#38D6D6]"></i>
                    <span className="font-grotesk text-sm text-[#38D6D6]">
                      Request received. We&apos;ll be in touch within 24 hours.
                    </span>
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <i className="ri-error-warning-line text-red-400"></i>
                    <span className="font-grotesk text-sm text-red-400">
                      Something went wrong. Please try again.
                    </span>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
